/**
 * What Boone knows — the memory screen's reads and writes (issue #302).
 *
 * **Three endpoints and no fourth.** `GET /memory` is #299's paged read; `POST /remember` and
 * `POST /forget-this` are #298's routes onto the tools themselves. There is no panel-shaped
 * write here and there is not meant to be: a deletion from this screen *is* `forget_this` —
 * the same plain function, the same tier, the same `PROPOSAL#`, the same `ACTION#` and the
 * same #114 scrub — which is the whole of ADR 0021 decision 5.
 *
 * **The response types are declared here rather than imported**, which is ADR 0021 decision 6:
 * the shared contract package waits until mobile is a real second caller, and until then this
 * app carries its own. They are a *subset* of what the server discloses, deliberately — a
 * field the screen does not render is a field this file should not claim to understand.
 *
 * **Nothing here decides a tier, and nothing here decides whether to ask.** The server answers
 * `status: "proposed"` or `"applied"` and the screen renders whichever arrived. That is not
 * politeness: the tiers are the tools' own (`confirmation.ts`), they differ per kind and per
 * collision, and a panel that predicted them would be a second implementation of the rule that
 * decides whether a family is asked before their record changes.
 */

import { type AppApi, appFetch } from "./client";
import {
  confirmWrite,
  familyPath,
  newIdempotencyKey,
  postWrite,
  type WriteEnvelope,
} from "./writes";

/**
 * Re-exported so the memory screen's imports stay one module deep (issue #310 moved the
 * bodies out).
 *
 * These three are the plumbing every panel write shares — see `writes.ts`, which exists
 * because this file and `roster.ts` each carried an identical copy and #310 was about to
 * write a third.
 */
export { confirmWrite, newIdempotencyKey, type WriteEnvelope };

/** One durable fact, as `disclosure.ts` shows it to the family it belongs to. */
export interface MemoryFact {
  /** `<TYPE>/<slug>` — a record reference, and the screen's stable React key. */
  readonly ref: string;
  /** One of the five kinds. A plain string on the wire; narrow it before rendering a heading. */
  readonly kind: string;
  readonly title: string;
  readonly content: string;
  /**
   * The family's own words behind the fact.
   *
   * Disclosed to this surface and to no model-facing one — ADR 0012 decision 6's rule is
   * scoped to a *host*, and `disclosure.ts` states the exception for the family themselves.
   */
  readonly rawText: string;
  readonly subject:
    | { readonly kind: "family" }
    | { readonly kind: "member"; readonly name: string | null }
    | { readonly kind: "unstated" };
  readonly subjectMemberId: string | null;
  readonly importance: string;
  readonly rationale: string | null;
  /**
   * `active` or `archived`.
   *
   * Rendered rather than filtered: this read deliberately drops the status filter, and an
   * archived fact shown as a live one is the "presents uncertain memories as facts" failure.
   */
  readonly status: string;
  /** When retrieval stops consulting this, if ever (ADR 0003 decision 10 — a filter, not a delete). */
  readonly expiresAt: string | null;
  readonly updatedAt: string;
}

export interface MemoryPage {
  readonly records: readonly MemoryFact[];
  /** Hand back as `?after=`. `null` means that was the last page. */
  readonly nextCursor: string | null;
}

/** The fact a colliding `remember` would overwrite — shown before the family answers (#92). */
export interface CollisionSummary {
  readonly title: string;
  readonly content: string;
}

export interface RememberEnvelope extends WriteEnvelope {
  readonly collision: CollisionSummary | null;
}

/** What `forget_this` is about to take, in the family's terms (#114, #241). */
export interface ForgetTarget {
  readonly title: string;
  /** Entries in the record of changes that quote this fact and will be scrubbed with it. */
  readonly auditEntries: number;
  /**
   * How many **other** saved facts recorded the identical words and will lose them (#241).
   *
   * The disclosure that matters most on this screen: it announces that something the family
   * asked to keep is being *edited* as a side effect of deleting something else.
   */
  readonly quotedOnOtherFacts: number;
}

export interface ForgetEnvelope extends WriteEnvelope {
  /** Present while proposing; `null` once applied, where naming the fact would put it back. */
  readonly deleting: ForgetTarget | null;
  /** What deletion does not reach, in the server's own sentences. */
  readonly doesNotReach: readonly string[];
}

/** Whose a fact is, as `remember` takes it (#92) — stated, never inferred from silence. */
export type FactSubject = { kind: "family" } | { kind: "member"; memberId: string };

/**
 * One page of the record.
 *
 * **`limit` is stated rather than defaulted** (PR #336 review, finding 5, which asked for this
 * across both walking screens rather than leaving them to diverge). An absent one falls back to
 * `DEFAULT_PAGE_SIZE` — fifty — so reading the whole record took four times the round trips it
 * needed. `pageSize` clamps at `MAX_PAGE_SIZE`, and a short page is not a lost record: the
 * cursor is followed either way.
 */
export function fetchMemoryPage(
  api: AppApi,
  familyId: string,
  after: string | null,
  limit: number,
): Promise<MemoryPage> {
  const query = new URLSearchParams({ limit: String(limit) });
  if (after !== null) query.set("after", after);
  return appFetch<MemoryPage>(api, familyPath(familyId, `/memory?${query.toString()}`));
}

/** What the screen sends to save a fact — new, or a correction to one that exists. */
export interface RememberBody {
  readonly contextType: string;
  readonly title: string;
  readonly content: string;
  readonly subject: FactSubject;
  /**
   * The words the person typed.
   *
   * This is what makes `provenance: "directed"` honestly theirs rather than something the
   * panel manufactured (ADR 0014 decision 4): they typed it, into a field under a heading they
   * chose, and it lands on a record they can read back.
   */
  readonly rawText: string;
  readonly idempotencyKey: string;
}

export function proposeRemember(
  api: AppApi,
  familyId: string,
  body: RememberBody,
): Promise<RememberEnvelope> {
  return postWrite<RememberEnvelope>(api, familyId, "/remember", {
    ...body,
    provenance: "directed",
  });
}

/**
 * Correct one fact (issues #83, #306) — **one write, whatever the family changed**.
 *
 * `remember` upserts on a title-derived key, so sending a corrected *title* through it creates
 * a second fact and leaves the first live. `correct_this` is addressed by the fact instead, so
 * a changed name moves the row and a changed sentence rewrites it, in one transaction and
 * behind one confirmation — which is what lets the Edit form be one form.
 *
 * Only what changed is sent. Absent means unchanged, so the panel cannot blank a field by
 * failing to mention it — which is the trap `remember` set for #302 and `carriedOver` had to
 * work around.
 *
 * No `provenance`, unlike {@link proposeRemember}: the tool has no such field, which is what
 * makes it always propose (ADR 0014 decision 4's structural floor).
 */
export function proposeCorrection(
  api: AppApi,
  familyId: string,
  body: {
    contextType: string;
    title: string;
    newTitle?: string;
    content?: string;
    subject?: FactSubject;
    idempotencyKey: string;
  },
): Promise<WriteEnvelope> {
  return postWrite<WriteEnvelope>(api, familyId, "/correct-this", { ...body });
}

export function proposeForget(
  api: AppApi,
  familyId: string,
  body: { contextType: string; title: string; idempotencyKey: string },
): Promise<ForgetEnvelope> {
  return postWrite<ForgetEnvelope>(api, familyId, "/forget-this", { ...body });
}
